import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Youtube, Mail } from "lucide-react";
import { api } from "../lib/api";
import { BlogSeo } from "../components/BlogSeo";
import { CornerWebs } from "../components/CornerWebs";

const SITE = "https://tattooedabroad.com";
const YOUTUBE_URL = "https://www.youtube.com/@TattooedAbroad";

const formatDate = (d) => {
  try {
    return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return d;
  }
};

// Render raw body text: blocks split by blank lines; "## " prefix = section heading.
const ArticleBody = ({ body }) => {
  const blocks = (body || "").split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  return (
    <div className="space-y-6">
      {blocks.map((block, i) => {
        if (block.startsWith("## ")) {
          return (
            <h2 key={i} className="pt-4 font-display text-2xl font-normal text-white md:text-3xl">
              {block.replace(/^##\s+/, "")}
            </h2>
          );
        }
        if (block.startsWith("# ")) {
          return (
            <h2 key={i} className="pt-4 font-display text-2xl font-normal text-white md:text-3xl">
              {block.replace(/^#\s+/, "")}
            </h2>
          );
        }
        const liLines = block.split("\n").map((l) => l.trim()).filter(Boolean);
        if (liLines.length && liLines.every((l) => l.startsWith("- "))) {
          return (
            <ul key={i} className="list-disc space-y-2 pl-6 font-body text-lg leading-relaxed text-white/75 marker:text-flame">
              {liLines.map((l, j) => (
                <li key={j}>{l.replace(/^-\s+/, "")}</li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} className="font-body text-lg leading-relaxed text-white/75">
            {block.split("\n").map((line, j) => (
              <span key={j}>
                {line}
                {j < block.split("\n").length - 1 && <br />}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
};

export default function TattooTimesArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    setStatus("loading");
    api.get(`/blog/articles/${slug}`)
      .then((res) => { setArticle(res.data); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-ink">
        <Loader2 className="animate-spin text-flame" size={30} />
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-6 bg-ink px-6 text-center">
        <h1 className="font-display text-3xl text-white">Article not found</h1>
        <Link to="/tattoo-times" className="inline-flex items-center gap-2 border border-flame px-6 py-3 font-body text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-flame hover:text-ink">
          <ArrowLeft size={15} /> Back to Tattoo Times
        </Link>
      </div>
    );
  }

  const canonical = `${SITE}/tattoo-times/${article.slug}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    description: article.seo_description || article.excerpt,
    author: { "@type": "Person", name: article.author },
    datePublished: article.date,
    dateModified: (article.updated_at || article.date || "").slice(0, 10),
    mainEntityOfPage: canonical,
    publisher: { "@type": "Organization", name: "Tattooed Abroad" },
    ...(article.photo ? { image: article.photo } : {}),
  };

  return (
    <>
      <BlogSeo
        title={article.seo_title || `${article.title} | Tattooed Abroad`}
        description={article.seo_description || article.excerpt}
        canonical={canonical}
        jsonLd={jsonLd}
      />
      <article className="relative overflow-hidden bg-ink pt-28 pb-20 md:pt-32 md:pb-24">
        <div className="pointer-events-none absolute left-1/2 top-40 z-0 h-[420px] w-[460px] -translate-x-1/2 rounded-full bg-flame opacity-[0.10] blur-[160px]" />
        <CornerWebs />
        <div className="relative z-10 mx-auto max-w-3xl px-6">
          <Link to="/tattoo-times" data-testid="back-to-tattoo-times" className="group inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.25em] text-flame">
            <ArrowLeft size={15} className="transition-transform group-hover:-translate-x-1" /> Back to Tattoo Times
          </Link>

          <header className="mt-8">
            <h1 data-testid="article-title" className="font-display text-4xl font-light leading-[1.1] tracking-tight text-white md:text-5xl">{article.title}</h1>
            <p className="mt-5 font-body text-sm uppercase tracking-[0.2em] text-white/45">
              By {article.author} · {formatDate(article.date)}
            </p>
          </header>

          {article.photo && (
            <img src={article.photo} alt={article.title} className="mt-10 w-full border border-white/10 object-cover" />
          )}

          <div className="mt-10">
            <ArticleBody body={article.body} />
          </div>

          <div className="mt-14 border-t border-white/10 pt-10">
            <p className="font-gothic text-xs uppercase tracking-[0.3em] text-flame">Stay on the journey</p>
            <div className="mt-5 flex flex-wrap gap-4">
              <Link
                to="/home#join"
                data-testid="article-newsletter-link"
                className="inline-flex items-center gap-2 border border-flame px-6 py-3.5 font-body text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-flame hover:text-ink"
              >
                <Mail size={15} /> Join the newsletter
              </Link>
              <a
                href={YOUTUBE_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="article-youtube-link"
                className="inline-flex items-center gap-2 border border-white/20 px-6 py-3.5 font-body text-xs uppercase tracking-[0.2em] text-white/80 transition-colors hover:border-flame hover:text-flame"
              >
                <Youtube size={16} /> Subscribe on YouTube
              </a>
            </div>
          </div>
        </div>
      </article>
    </>
  );
}
